<template>
    <div :class="framed ? 'rounded-lg border border-gray-200 bg-gray-100 p-3' : ''">
        <div class="relative">
            <!-- The same drag track the related-products slider uses, so a set of any length stays on
                 one row instead of pushing the price and the button off the screen. -->
            <DragScrollTrack ref="track" class="gap-6" snap>
                <!-- Two positions split the row and nothing slides. From three up: 1.5 columns on
                     mobile, 2.5 from sm up, so it always reads as a slider with more to the right.
                     The gap is subtracted so the half column fits rather than overflowing. -->
                <div
                    v-for="(column, index) in setStore.positionColumns"
                    :key="column.filter.meta.name"
                    :class="[
                        'shrink-0 snap-start [&_label]:hidden',
                        setStore.positionColumns.length <= 2
                            ? 'w-[calc((100%-1.5rem)/2)]'
                            : 'w-[calc((100%-1.5rem)/1.5)] sm:w-[calc((100%-3rem)/2.5)]',
                    ]"
                >
                    <div class="relative mb-2 aspect-square w-full overflow-hidden rounded-lg bg-gray-100">
                        <img
                            v-if="column.image"
                            :src="column.image.url"
                            :alt="column.image.alt ?? column.filter.meta.label"
                            class="h-full w-full object-cover"
                            loading="lazy"
                        />

                        <span
                            class="absolute top-1.5 left-1.5 flex size-6 items-center justify-center rounded-full bg-white/90 text-xs font-semibold shadow"
                            v-text="index + 1"
                        />
                    </div>

                    <FilterSelectMenu
                        :group="column.filter"
                        :model-value="setStore.selectedFilterOptions[column.filter.meta.name]"
                        :hide-badge="productStore.configurableProduct.is_hiding_most_chosen_label"
                        :show-placeholder="setStore.forceSelectionActive"
                        show-prices
                        teleport
                        @update:model-value="setStore.selectOption(column.filter.meta.name, $event)"
                    />
                </div>
            </DragScrollTrack>

            <button
                v-if="canScrollLeft"
                type="button"
                class="absolute top-1/2 left-1.5 -translate-y-1/2 cursor-pointer rounded-full bg-white p-2 shadow-md"
                @click="scrollByColumn(-1)"
            >
                <ChevronLeftIcon class="size-6" />
            </button>
            <button
                v-if="canScrollRight"
                type="button"
                class="absolute top-1/2 right-1.5 -translate-y-1/2 cursor-pointer rounded-full bg-white p-2 shadow-md"
                @click="scrollByColumn(1)"
            >
                <ChevronRightIcon class="size-6" />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import FilterSelectMenu from '@/components/forms/select-menus/FilterSelectMenu.vue';
import DragScrollTrack from '@/components/slider/DragScrollTrack.vue';
import { useProductSetStore } from '@/stores/products/ProductSetStore';
import { useProductStore } from '@/stores/products/ProductStore';
import { ChevronLeftIcon, ChevronRightIcon } from '@lucide/vue';
import { nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';

// The gray panel frame; the mobile sheet brings its own chrome and drops it.
withDefaults(defineProps<{ framed?: boolean }>(), { framed: true });

const productStore = useProductStore();
const setStore = useProductSetStore();

const track = useTemplateRef<InstanceType<typeof DragScrollTrack>>('track');

const canScrollLeft = ref(false);
const canScrollRight = ref(false);

function trackElement(): HTMLElement | null {
    return track.value?.trackRef ?? null;
}

/** Where each column starts, in scroll coordinates. */
function columnOffsets(element: HTMLElement): number[] {
    const trackLeft = element.getBoundingClientRect().left;

    return Array.from(element.children).map((column: Element) => column.getBoundingClientRect().left - trackLeft + element.scrollLeft);
}

/** Steps to the neighbouring column, clamped so the track never scrolls past its end. */
function scrollByColumn(direction: number): void {
    const element = trackElement();

    if (!element) {
        return;
    }

    const offsets = columnOffsets(element);
    const target =
        direction > 0
            ? offsets.find((offset: number) => offset > element.scrollLeft + 1)
            : offsets.reverse().find((offset: number) => offset < element.scrollLeft - 1);

    if (target === undefined) {
        return;
    }

    element.scrollTo({ left: Math.min(target, element.scrollWidth - element.clientWidth), behavior: 'smooth' });
}

function updateArrows(): void {
    const element = trackElement();

    if (!element) {
        return;
    }

    canScrollLeft.value = element.scrollLeft > 0;
    // A pixel of slack: fractional widths leave scrollLeft just short of the real end.
    canScrollRight.value = element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
}

onMounted(async () => {
    await nextTick();
    updateArrows();

    trackElement()?.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
});

onBeforeUnmount(() => {
    trackElement()?.removeEventListener('scroll', updateArrows);
    window.removeEventListener('resize', updateArrows);
});
</script>
